import { ConfigConstants, FormatDurationPipe, StatusBar } from '@evorait/pace-core';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';

/**
 * The status-bar type and text for the current day's work duration, shared by the legacy
 * `WeeklyWorkTimeComponent` and the NgRx `WeeklyWorkTimeNgrxComponent` so this ~40-line rule set
 * has exactly one place to change.
 *
 * Check, if Work duration exceeds NINE hours but less than TEN hours - status bar color should be
 * light orange. Check if Work duration exceeds TEN hours - status bar color should be dark orange.
 * Otherwise the bar reflects whether work has started for today.
 *
 * @param currentWorkDurationInMinutes minutes worked today (committed + in progress)
 * @param isWorkStartedForToday whether the technician has started work today
 * @param translate resolves the status text
 * @param formatDuration formats the duration into the text
 * @returns the status bar to show
 */
export function getStatusBarDetails(
    currentWorkDurationInMinutes: number,
    isWorkStartedForToday: boolean,
    translate: TranslateService,
    formatDuration: FormatDurationPipe,
): StatusBar {
    const workStatusBar = new StatusBar();
    const formattedWorkDuration = formatDuration.transform(currentWorkDurationInMinutes);
    const nineHoursInMinutes = moment.duration(ConfigConstants.nineHoursWorkDuration, 'hours').asMinutes();
    const totalWorkingMinutes = moment.duration(ConfigConstants.totalWorkingHours, 'hours').asMinutes();

    if (currentWorkDurationInMinutes >= nineHoursInMinutes && currentWorkDurationInMinutes < totalWorkingMinutes) {
        workStatusBar.type = ConfigConstants.STATUSBAR_TYPE.WARNING;
        workStatusBar.text = <string>translate.instant('WEEKLY_WORK_WORK_TODAY_IN_PROGRESS_LABEL', {
            workHours: formattedWorkDuration,
        });
        return workStatusBar;
    }

    if (currentWorkDurationInMinutes >= totalWorkingMinutes) {
        workStatusBar.type = ConfigConstants.STATUSBAR_TYPE.CUSTOM;
        workStatusBar.text = <string>translate.instant('WEEKLY_WORK_WORK_HOUR_EXCEEDED_LABEL', {
            workHours: formattedWorkDuration,
        });
        return workStatusBar;
    }

    if (isWorkStartedForToday) {
        workStatusBar.type = ConfigConstants.STATUSBAR_TYPE.DEFAULT;
        workStatusBar.text = <string>translate.instant('WEEKLY_WORK_WORK_TODAY_IN_PROGRESS_LABEL', {
            workHours: formattedWorkDuration,
        });
        return workStatusBar;
    }

    workStatusBar.type = ConfigConstants.STATUSBAR_TYPE.SUCCESS;
    workStatusBar.text = <string>translate.instant('WEEKLY_WORK_WORK_TODAY_COMPLETED_LABEL', {
        workHours: formattedWorkDuration,
    });
    return workStatusBar;
}
